import { FC, useMemo } from "react";
import { useTranslations, cx, priceDisplay } from "@jugl-web/utils";
import { Tooltip } from "@jugl-web/ui-components";
import { Plan } from "@jugl-web/rest-api";
import { DEFAULT_ENTITY_CURRENCY } from "@jugl-web/utils/consts";
import { ReactComponent as BricksIcon } from "./assets/bricks.svg";
import { ReactComponent as CheckIcon } from "./assets/check.svg";
import { ReactComponent as InfoIcon } from "./assets/info.svg";
import { ReactComponent as CheckOutlinedIcon } from "./assets/check-outlined.svg";
import { usePlansData } from "../../../../hooks/usePlansData";
import { PlanActionButton } from "../PlanActionButton";

type PlanBadgeVariant = "mostPopular" | "currentPlan";

/** Badge shown on the most‑popular or current plan */
const PlanBadge: FC<{ label: string; variant: PlanBadgeVariant }> = ({
  label,
  variant,
}) => (
  <div
    className={cx(
      "absolute top-[-15px] right-6 z-10 flex h-[29px] w-[111px] items-center justify-center rounded-xl px-4 py-1.5 text-xs font-medium text-white",
      variant === "mostPopular" &&
        "bg-gradient-to-r from-[#506FE2] to-[#1C70DF]",
      variant === "currentPlan" && "bg-[#22A16B]"
    )}
  >
    {label}
  </div>
);

/** Price block – handles enterprise icon */
const PriceBlock: FC<{
  type: string;
  price: number;
  currency: string;
  originalMonthlyPrice?: number;
}> = ({ type, price, currency, originalMonthlyPrice }) => (
  <div className="flex items-center gap-2">
    {type !== "enterprise" && originalMonthlyPrice != null && (
      <span className="text-xl text-[#8F979A] line-through">
        {priceDisplay(originalMonthlyPrice, currency, false, {
          minimumFractionDigits: 0,
        })}
      </span>
    )}
    <div className="text-[32px] font-medium text-dark">
      {type !== "enterprise" &&
        priceDisplay(price, currency, false, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}
      {type === "enterprise" && <BricksIcon />}
    </div>
  </div>
);

/** Single feature row – shows a check icon based on selection */
const FeatureItem: FC<{
  feature: { name: string; tooltip?: string };
  isSelected: boolean;
}> = ({ feature, isSelected }) => {
  const hasTooltip = !!feature.tooltip;
  return (
    <Tooltip
      placement="top"
      isDisabled={!hasTooltip}
      renderTrigger={({ props, ref }) => (
        <div
          ref={ref}
          {...props}
          className={cx(
            "group flex items-start gap-2 text-sm leading-[21px] text-dark-900",
            hasTooltip && "cursor-pointer"
          )}
        >
          <div className="mt-0.5 flex shrink-0 items-center">
            {isSelected ? <CheckIcon /> : <CheckOutlinedIcon />}
          </div>
          <div
            className={cx(
              hasTooltip &&
                "group-hover:decoration-thickness-[1.5px] group-hover:font-medium group-hover:underline group-hover:decoration-[#DB99F1] group-hover:decoration-wavy group-hover:underline-offset-[2.3px]"
            )}
          >
            {feature.name}
          </div>
          {hasTooltip && <InfoIcon className="mt-[3px] ml-auto shrink-0" />}
        </div>
      )}
    >
      {feature.tooltip}
    </Tooltip>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

type PlanCardProps = {
  plan: Plan;
  allPlans: Plan[];
  name: string;
  isSelected: boolean;
  isCurrentPlan?: boolean;
  isMostPopular?: boolean;
  type: "project" | "crm" | "aio" | "enterprise";
  interval: "month" | "year";
  onSelect: () => void;
  onButtonClick?: () => void;
  isDowngradeDisabled?: boolean;
  downgradeDisabledTooltip?: React.ReactNode;
};

export const PlanCard: FC<PlanCardProps> = ({
  plan,
  allPlans,
  name,
  type,
  interval,
  isCurrentPlan,
  isMostPopular = false,
  onSelect,
  onButtonClick,
  isDowngradeDisabled,
  downgradeDisabledTooltip,
}) => {
  const { plansFeatures } = usePlansData(allPlans);
  const { t } = useTranslations();

  // Resolve price for the selected interval
  const { price, currency, originalMonthlyPrice } = useMemo(() => {
    const priceObj = plan.prices.find((p) => p.interval === interval);
    const curr = priceObj?.currency || DEFAULT_ENTITY_CURRENCY;

    if (interval === "year" && priceObj) {
      const monthlyPriceObj = plan.prices.find((p) => p.interval === "month");
      return {
        price: Math.round(priceObj.unit_amount / 12),
        currency: curr,
        originalMonthlyPrice: monthlyPriceObj?.unit_amount,
      };
    }

    return {
      price: priceObj?.unit_amount || 0,
      currency: curr,
      originalMonthlyPrice: undefined,
    };
  }, [plan, interval]);

  const featuresData =
    plansFeatures.find((f) => f.id === plan.id) ?? plansFeatures[0];

  const isFree = plan.prices.every((p) => p.unit_amount === 0);

  return (
    <div
      className={cx(
        "relative min-h-[751px] w-[328px] shrink-0 cursor-pointer rounded-2xl border-[1px] border-solid border-[#D3DDFF] bg-white px-7 pt-8 pb-12 font-secondary transition-colors"
      )}
      onClick={onSelect}
    >
      {isCurrentPlan ? (
        <PlanBadge
          variant="currentPlan"
          label={t({
            id: "subscription-plans-dialog.current-plan",
            defaultMessage: "Current Plan",
          })}
        />
      ) : isMostPopular ? (
        <PlanBadge
          variant="mostPopular"
          label={t({
            id: "subscription-plans-dialog.plan-card-badge",
            defaultMessage: "Most Popular",
          })}
        />
      ) : null}

      {/* Plan title */}
      <div className="flex h-[86px] flex-col gap-1">
        <span className="font-secondary text-3xl leading-[42px] font-semibold text-dark">
          {name}
        </span>
        <div className="text-sm leading-[21px] font-normal text-dark-700">
          {featuresData?.subtitle}
        </div>
      </div>

      {/* Price */}
      <div className="flex gap-2 pt-8">
        <PriceBlock
          type={type}
          price={price}
          currency={currency}
          originalMonthlyPrice={originalMonthlyPrice}
        />
        <div className="flex items-center text-sm text-dark-700">
          {isFree
            ? t({
                id: "subscription.plans.free-forever",
                defaultMessage: "Free forever",
              })
            : t({
                id: "subscription.plans.per-month",
                defaultMessage: "per month",
              })}
        </div>
      </div>

      {/* Primary action */}
      <div className="flex items-center pt-10">
        {isDowngradeDisabled && downgradeDisabledTooltip ? (
          <Tooltip
            placement="top"
            className="max-w-[300px] rounded-[20px] p-4"
            renderTrigger={({ props, ref }) => (
              <div className="w-full" ref={ref} {...props}>
                <PlanActionButton
                  plan={plan}
                  allPlans={allPlans}
                  selectedInterval={interval}
                  size="lg"
                  fullWidth
                  isMostPopular={isMostPopular}
                  isDisabled
                />
              </div>
            )}
          >
            {downgradeDisabledTooltip}
          </Tooltip>
        ) : (
          <PlanActionButton
            plan={plan}
            allPlans={allPlans}
            selectedInterval={interval}
            size="lg"
            fullWidth
            isMostPopular={isMostPopular}
            onClick={onButtonClick}
          />
        )}
      </div>

      <div className="-mx-7 my-[34px] h-[1px] bg-[#D3DDFF]" />
      <div className="font-medium text-dark">{featuresData.header}</div>

      <div className="flex flex-col gap-[14px] pt-5">
        {featuresData?.features?.map((feature) => (
          <FeatureItem
            key={feature.name}
            feature={feature}
            isSelected={isMostPopular}
          />
        ))}
      </div>
    </div>
  );
};
